import { fg } from '@atlaskit/platform-feature-flags/fg';
import { getBackendExperiment } from '@atlassian/search-experiment/backend';
import { type ExperimentExperience } from '@atlassian/search-experiment/dynamic-config';
import {
	type SearchMatchingLayerExperimentsMetadata,
	type GetLayerValuesFn,
	type ProductDataRegions,
	type ProductEdition,
	type SearchExperimentLayer,
	type ThirdPartyFiltersForExperiment,
} from '@atlassian/search-experiment/dynamic/types';
import { getExperimentConfig } from '@atlassian/search-experiment/statsig';
import type { BackendExperimentLayerOverride } from '@atlassian/search-experiment/statsig/dynamic';
import { type BackendExperiment } from '@atlassian/search-experiment/types';

export type SearchExperimentConfig = {
	experimentId: string | undefined;
	experimentLayers: SearchExperimentLayer[] | undefined;
	shadowExperimentId: string | undefined;
	fanoutExperimentCohort: string | undefined;
	matchingLayerExperimentsMetadata: SearchMatchingLayerExperimentsMetadata | undefined;
	/** The full backend experiment object, used for analytics/tracking */
	backendExperiment: BackendExperiment;
};

export type SearchExperimentConfigProps = {
	/** Experiment layers from the host product configuration */
	experimentLayers: string[];
	/** The experience layer — search page or quick find */
	experienceLayer: ExperimentExperience;
	/** The resolved entities from filter selection */
	entities: string[];
	/** The product edition (e.g. free, standard, premium) */
	productEdition: ProductEdition;
	/** Whether the current site is a Hello site */
	isHello: boolean;
	/** Product data regions configuration */
	productDataRegions: ProductDataRegions;
	/** The cloud ID */
	cloudId: string;
	/** Whether Jira issue indexing is enabled */
	isJiraIssueIndexed: boolean;
	/** Third-party filter configuration for experiment resolution */
	thirdPartyFilters: ThirdPartyFiltersForExperiment;
	/** Backend experiment layer overrides */
	backendExperimentOverrides: BackendExperimentLayerOverride[] | undefined;
	/** Error handler for statsig operations */
	onError: (error: Error) => void;
	/** Optional SSR-compatible layer value resolver */
	getLayerValuesFn?: GetLayerValuesFn;
};

export type GetSearchExperimentConfig = (
	props: SearchExperimentConfigProps,
) => SearchExperimentConfig;

/**
 * Resolves experiment configuration for the search query from host product
 * props and runtime context.
 *
 * Composes:
 * 1. getExperimentConfig (from search-experiment) - resolves Statsig experiment layers
 * 2. getBackendExperiment - resolves the active backend experiment based on entities and context
 * 3. fanoutExperimentCohort - determines the 3P grouping cohort
 */
export const getSearchExperimentConfig: GetSearchExperimentConfig = (props) => {
	const experimentConfig = getExperimentConfig({
		experimentLayers: props.experimentLayers,
		sideBySideExperiment: undefined,
		statsigLayer: undefined,
		onError: props.onError,
		getLayerValuesFn: props.getLayerValuesFn,
	});

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities: props.entities,
		productEdition: props.productEdition,
		isHello: props.isHello,
		productDataRegions: props.productDataRegions,
		experience: props.experienceLayer,
		tenantId: props.cloudId,
		isJiraIssueIndexed: props.isJiraIssueIndexed,
		thirdPartyFilters: props.thirdPartyFilters,
		backendExperimentOverrides: props.backendExperimentOverrides,
		getLayerValuesFn: props.getLayerValuesFn,
	});

	const { experimentId, shadowExperimentId, experimentLayers, matchingLayerExperimentsMetadata } =
		backendExperiment || {};

	return {
		experimentId,
		experimentLayers,
		matchingLayerExperimentsMetadata: fg('search_experiment_layer_metadata')
			? matchingLayerExperimentsMetadata
			: undefined,
		shadowExperimentId,
		fanoutExperimentCohort: 'enable-v2',
		backendExperiment,
	};
};
